import { useEffect, useMemo, useState } from "react";
import { type CaseSubset, analyzeCases, durationProfile } from "./processCaseNotes";
import {
  EMPTY_INSTANCE_SCOPE,
  type InstanceScope,
  instanceScopeFromIds,
  matchInstanceScope,
} from "./processScopeModel";
import { matchRun } from "./processSearch";
import type { ProcessGraphState } from "./useProcessGraph";

/**
 * Ways of finding cases in the list. Unlike the scope they never reach the graph, the KPIs or the
 * other tabs: they sit on the table, below the readings they would otherwise change out of view.
 */
export type CaseListFilters = {
  search: string;
  subset: CaseSubset;
  /** The cases of one path, as the Overview hands them over. */
  path: { ids: string[]; label: string } | null;
};

export const EMPTY_CASE_LIST_FILTERS: CaseListFilters = {
  search: "",
  subset: "all",
  path: null,
};

/** Filters apply immediately; only the optional hand-picked graph has a draft. */
export function useProcessCaseSelection(state: ProcessGraphState) {
  const { appliedScope, scopeIndex, applyScope, resolveScopeQuery } = state;
  const filters = useMemo(
    () => ({
      ...appliedScope,
      commonPaths: false,
      excludedIds: [],
    }),
    [appliedScope],
  );
  const durationReferenceInstances = useMemo(() => {
    const ids = new Set(
      matchInstanceScope(scopeIndex, {
        ...filters,
        minDuration: undefined,
        maxDuration: undefined,
      }).matchedIds,
    );
    return scopeIndex.instances.filter(instance => ids.has(instance.id));
  }, [scopeIndex, filters]);
  const durationReference = useMemo(
    () => durationProfile(durationReferenceInstances.map(instance => instance.source.avgDuration)),
    [durationReferenceInstances],
  );
  const base = useMemo(() => {
    const ids = new Set(matchInstanceScope(scopeIndex, filters).matchedIds);
    return analyzeCases(
      scopeIndex.instances.filter(instance => ids.has(instance.id)),
      durationReference.outlierAbove,
    );
  }, [scopeIndex, filters, durationReference]);
  // Kept here rather than in the tab, so the list comes back as it was left after a tab visit.
  const [list, setList] = useState(EMPTY_CASE_LIST_FILTERS);
  const matching = useMemo(() => {
    const subset = base.subsets[list.subset];
    const path = list.path && new Set(list.path.ids);
    const search = list.search.trim();
    return new Set(
      base.cases
        .map(reading => reading.instance)
        .filter(
          instance =>
            subset.has(instance.id) &&
            (!path || path.has(instance.id)) &&
            (!search || matchRun(scopeIndex, instance, search) !== null),
        )
        .map(instance => instance.id),
    );
  }, [base, list, scopeIndex]);
  const graphKey = JSON.stringify([appliedScope.commonPaths, appliedScope.excludedIds]);
  const [draft, setDraft] = useState<{
    source: string;
    picks: Set<string> | null;
  }>({
    source: graphKey,
    picks: null,
  });
  if (draft.source !== graphKey) setDraft({ source: graphKey, picks: null });
  const picks = draft.picks ?? new Set<string>();
  // A pick hidden by the list's search or subsets is still in scope, so the graph still draws it.
  const includedIds = base.cases.map(reading => reading.instance.id).filter(id => picks.has(id));
  // What the graph draws when it holds hand-picked cases, rather than every case in scope.
  const graphIds = useMemo(
    () => (appliedScope.excludedIds.length > 0 ? new Set(state.selectedInstanceIds) : null),
    [appliedScope.excludedIds, state.selectedInstanceIds],
  );
  const drawOnly = (ids: Set<string>) =>
    applyScope({
      ...appliedScope,
      commonPaths: false,
      excludedIds: scopeIndex.order.filter(id => !ids.has(id)),
    });
  const changeFilters = (next: InstanceScope) =>
    applyScope({
      ...next,
      commonPaths: appliedScope.commonPaths,
      excludedIds: appliedScope.excludedIds,
    });
  // Demo actions stay available even when Cases is not the active tab.
  useEffect(() => {
    const reset = (event: Event) => {
      const ids = (event as CustomEvent<{ instanceIds?: string[] }>).detail?.instanceIds;
      applyScope(
        ids?.length
          ? instanceScopeFromIds(scopeIndex, ids)
          : {
              ...appliedScope,
              query: null,
              excludedIds: [],
              commonPaths: undefined,
            },
      );
    };
    const clear = () =>
      applyScope({
        ...appliedScope,
        excludedIds: [],
        commonPaths: undefined,
      });
    let cancelled = false;
    const query = async (event: Event) => {
      const resolved = await resolveScopeQuery(
        (event as CustomEvent<{ query: string }>).detail.query,
      );
      if (!cancelled && resolved)
        applyScope({
          ...appliedScope,
          query: resolved,
          excludedIds: [],
          commonPaths: false,
        });
    };
    window.addEventListener("tour:reset-instance-scope", reset);
    window.addEventListener("tour:clear-hand-picked", clear);
    window.addEventListener("tour:apply-scope-query", query);
    return () => {
      cancelled = true;
      window.removeEventListener("tour:reset-instance-scope", reset);
      window.removeEventListener("tour:clear-hand-picked", clear);
      window.removeEventListener("tour:apply-scope-query", query);
    };
  }, [appliedScope, applyScope, scopeIndex, resolveScopeQuery]);

  return {
    filters,
    base,
    durationReference,
    durationReferenceInstances,
    list,
    changeList: setList,
    picks,
    matching,
    includedIds,
    graphIds,
    /** Draws exactly the cases the list shows. */
    showListInGraph: () => drawOnly(matching),
    editing: draft.picks !== null,
    hiddenPicks: includedIds.filter(id => !matching.has(id)).length,
    changeFilters,
    clearFilters: () => changeFilters({ ...EMPTY_INSTANCE_SCOPE }),
    begin: () =>
      setDraft({
        source: graphKey,
        picks: new Set(
          appliedScope.excludedIds.length
            ? scopeIndex.order.filter(id => !appliedScope.excludedIds.includes(id))
            : [],
        ),
      }),
    select: (ids: string[]) => setDraft({ source: graphKey, picks: new Set(ids) }),
    toggle: (id: string) =>
      setDraft(current => {
        const next = new Set(current.picks);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return { ...current, picks: next };
      }),
    apply: () => {
      drawOnly(picks);
      setDraft({ source: graphKey, picks: null });
    },
    cancel: () => setDraft({ source: graphKey, picks: null }),
  };
}

export type ProcessCaseSelection = ReturnType<typeof useProcessCaseSelection>;
